import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaTwitter,
  FaArrowRight,
} from "react-icons/fa";
import { Logo } from "../Logo/Logo";
import {
  LABORHACK_CAREERS_URL,
  LABORHACK_SUPPORT_EMAIL,
  LABORHACK_SUPPORT_PHONE,
} from "../../constants";
import { LaborHackSocialLinks } from "../../pages/contact";
import { useEmbed } from "../../hooks/useEmbed";
import ndprLogo from "../../assets/ndpr-logo.png";
import ndprQrCode from "../../assets/lh-ndpr-compliance-qr-code.jpeg";
import QRCode from "qrcode";

interface FooterV2Props {
  ctaTitle?: string;
  primaryCtaLabel?: string;
  onPrimaryCta?: () => void;
  primaryCtaHref?: string;
  secondaryCtaLabel?: string;
  onSecondaryCta?: () => void;
}

export const FooterV2: React.FC<FooterV2Props> = ({
  ctaTitle,
  primaryCtaLabel,
  onPrimaryCta,
  primaryCtaHref,
  secondaryCtaLabel,
  onSecondaryCta,
}) => {
  const { isEmbedded } = useEmbed();
  const { facebook, twitter, instagram, linkedin } = LaborHackSocialLinks;
  const [qrCodeUrl, setQrCodeUrl] = useState<string>(ndprQrCode);

  const ndprVerificationUrl =
    "https://services.ndpc.gov.ng/portal/?page=verify-c&d=ang39993901157&id=74558&sn=ae9570b968d1427c686fcc826f05ccc8&t=audit_filing&tp=nwp_audit";

  useEffect(() => {
    // generate QR code for NDPR verification URL
    QRCode.toDataURL(ndprVerificationUrl, {
      width: 44,
      margin: 1,
      color: {
        dark: "#000000",
        light: "#FFFFFF",
      },
    })
      .then((url: string) => {
        // replace the image string with the generated url
        setQrCodeUrl(url);
      })
      .catch((err: any) => {
        console.error("Error generating QR code:", err);
      });
  }, []);

  if (isEmbedded) {
    return null;
  }

  return (
    <footer className="w-full bg-[#040A0D] text-white">
      <div className="max-w-screen-2xl mx-auto">
        {ctaTitle && (onPrimaryCta || primaryCtaHref) && primaryCtaLabel && (
          <>
            {/* CTA Section */}
            <div className="w-full px-3 lg:px-20 py-10 lg:py-14 ">
              <div className="flex flex-col items-center text-center gap-6">
                <h2 className="max-w-[250px] md:max-w-[475px] text-[25px] md:text-[40px] leading-tight font-medium text-white font-[AeonikPro]">
                  {ctaTitle || "Start hiring with LaborHack"}
                </h2>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  {primaryCtaHref ? (
                    <a
                      href={primaryCtaHref}
                      className="min-w-[295px] bg-[#ED6E5C] text-[#FEFEFE] font-[Inter] font-medium text-base md:text-[18px] leading-[1.333] h-16 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                    >
                      {primaryCtaLabel || "Create a free account"}
                      <FaArrowRight className="w-3.5 lg:w-4 h-3.5 lg:h-4" />
                    </a>
                  ) : (
                    <button
                      onClick={onPrimaryCta}
                      className="min-w-[295px] bg-[#ED6E5C] text-[#FEFEFE] font-[Inter] font-medium text-base md:text-[18px] leading-[1.333] h-16 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                    >
                      {primaryCtaLabel || "Create a free account"}
                      <FaArrowRight className="w-3.5 lg:w-4 h-3.5 lg:h-4" />
                    </button>
                  )}
                  {secondaryCtaLabel && onSecondaryCta && (
                    <button
                      onClick={onSecondaryCta}
                      className="min-w-[295px] bg-transparent text-[#FEFEFE] font-[Inter] font-medium text-base md:text-[18px] leading-[1.333] h-16 flex items-center justify-center gap-2 border border-[#FEFEFE] hover:bg-white/10 transition-colors"
                    >
                      {secondaryCtaLabel || "Book a demo"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        <div className="w-full px-3 lg:px-20 pb-2 lg:pb-4">
          <div className="py-10 lg:py-14 grid grid-cols-1 lg:grid-cols-4 gap-10 border-t-[0.5px] border-white/20">
            {/* Services */}
            <div>
              <h6 className="text-base md:text-lg font-medium mb-6 text-[#A5A5A5] font-[Inter] leading-6">
                Services
              </h6>
              <ul className="space-y-4">
                <li>
                  <RouterLink
                    to="/homeowners"
                    className="text-base md:text-lg text-white hover:text-white/80 font-[Inter] font-normal leading-6"
                  >
                    For homeowners
                  </RouterLink>
                </li>
                <li>
                  <RouterLink
                    to="/businesses"
                    className="text-base md:text-lg text-white hover:text-white/80 font-[Inter] font-normal leading-6"
                  >
                    Find talent (Businesses)
                  </RouterLink>
                </li>
                <li>
                  <RouterLink
                    to="/community-manager"
                    className="text-base md:text-lg text-white hover:text-white/80 font-[Inter] font-normal leading-6"
                  >
                    Manage talent
                  </RouterLink>
                </li>
                <li>
                  <RouterLink
                    to="/skilled-workers"
                    className="text-base md:text-lg text-white hover:text-white/80 font-[Inter] font-normal leading-6"
                  >
                    Get jobs
                  </RouterLink>
                </li>
                <li>
                  <RouterLink
                    to="/jobs"
                    className="text-base md:text-lg text-white hover:text-white/80 font-[Inter] font-normal leading-6"
                  >
                    Job board
                  </RouterLink>
                </li>
                <li>
                  <RouterLink
                    to="/subscriptions"
                    className="text-base md:text-lg text-white hover:text-white/80 font-[Inter] font-normal leading-6"
                  >
                    Subscriptions
                  </RouterLink>
                </li>
                <li>
                  <RouterLink
                    to="/laborpass"
                    className="text-base md:text-lg text-white hover:text-white/80 font-[Inter] font-normal leading-6"
                  >
                    LaborPass Portfolio
                  </RouterLink>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h6 className="text-base md:text-lg font-medium mb-6 text-[#A5A5A5] font-[Inter] leading-6">
                Company
              </h6>
              <ul className="space-y-4">
                <li>
                  <RouterLink
                    to="/about"
                    className="text-base md:text-lg text-white hover:text-white/80 font-[Inter] font-normal leading-6"
                  >
                    About us
                  </RouterLink>
                </li>
                {/* <li>
                <RouterLink
                  to="/pricing"
                  className="text-base md:text-lg text-white hover:text-white/80 font-[Inter] font-normal leading-6"
                >
                  Pricing
                </RouterLink>
              </li> */}
                <li>
                  <a
                    href="https://blog.laborhack.com"
                    className="text-base md:text-lg text-white hover:text-white/80 font-[Inter] font-normal leading-6"
                  >
                    Blog
                  </a>
                </li>
                {/* <li>
                  <a
                    href={LABORHACK_CAREERS_URL}
                    className="text-base md:text-lg text-white hover:text-white/80 font-[Inter] font-normal leading-6"
                  >
                    Careers
                  </a>
                </li> */}
                <li>
                  <RouterLink
                    to="/faqs"
                    className="text-base md:text-lg text-white hover:text-white/80 font-[Inter] font-normal leading-6"
                  >
                    FAQs
                  </RouterLink>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h6 className="text-base md:text-lg font-medium mb-6 text-[#A5A5A5] font-[Inter] leading-6">
                Legal
              </h6>
              <ul className="space-y-4">
                <li>
                  <RouterLink
                    to="/privacy"
                    className="text-base md:text-lg text-white hover:text-white/80 font-[Inter] font-normal leading-6"
                  >
                    Privacy policy
                  </RouterLink>
                </li>
                <li>
                  <RouterLink
                    to="/terms"
                    className="text-base md:text-lg text-white hover:text-white/80 font-[Inter] font-normal leading-6"
                  >
                    Terms of service
                  </RouterLink>
                </li>
                <li>
                  <RouterLink
                    to="/cookie"
                    className="text-base md:text-lg text-white hover:text-white/80 font-[Inter] font-normal leading-6"
                  >
                    Cookie policy
                  </RouterLink>
                </li>
                <li>
                  <RouterLink
                    to="/pro-terms"
                    className="text-base md:text-lg text-white hover:text-white/80 font-[Inter] font-normal leading-6"
                  >
                    Artisan terms of service
                  </RouterLink>
                </li>
              </ul>
            </div>

            {/* Contact Us */}
            <div>
              <h6 className="text-base md:text-lg font-medium mb-6 text-[#A5A5A5] font-[Inter] leading-6">
                Contact us
              </h6>
              <ul className="space-y-4">
                <li>
                  <div className="text-base md:text-lg text-white font-[Inter] font-normal leading-7">
                    Dominion House 207, Igbosere
                    <br />
                    Road, Lagos Island,Lagos
                  </div>
                </li>
                <li>
                  <a
                    href={`mailto:${LABORHACK_SUPPORT_EMAIL}?subject=Hello%20LaborHack`}
                    className="text-base md:text-lg text-white hover:text-white/80 font-[Inter] font-normal leading-6"
                  >
                    {LABORHACK_SUPPORT_EMAIL}
                  </a>
                </li>
                <li>
                  <a
                    href={`tel:${LABORHACK_SUPPORT_PHONE}`}
                    className="text-base md:text-lg text-white hover:text-white/80 font-[Inter] font-normal leading-6"
                  >
                    {LABORHACK_SUPPORT_PHONE}
                  </a>
                </li>
                <li>
                  <div className="text-base md:text-lg text-white font-[Inter] font-normal leading-6">
                    Support Hours:
                    <br />
                    Monday - Saturday 9 AM - 6 PM
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Hidden but crawlable Glossary link */}
          <div
            className="absolute left-[-10000px] top-auto w-px h-px overflow-hidden"
            aria-hidden="true"
          >
            <RouterLink to="/glossary">View Glossary</RouterLink>
          </div>

          <div className="w-full border-y-[0.5px] border-white/20 my-10 py-8">
            <div className="flex flex-col lg:flex-row items-center lg:justify-between gap-5">
              <Logo width={216} height={30} fill="#FFF" />

              {/* NDPR compliance badge */}
              <a
                href={ndprVerificationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/30 transition-all duration-200 w-[220px] flex flex-col items-center justify-center"
                aria-label="Verify NDPR compliance certificate"
              >
                <div className="flex items-center justify-evenly w-full">
                  <img
                    src={ndprLogo}
                    alt="NDPR Logo"
                    className="h-20 w-[64px] py-1"
                  />
                  <div className="border-l-[0.5px] border-white/30 h-20"></div>
                  <img
                    src={qrCodeUrl}
                    alt="LaborHack NDPR QR Code"
                    className="h-16 w-16 "
                  />
                </div>
                <div className="p-4 w-full border-t-[0.5px] border-white/30">
                  <span className="text-sm lg:text-base text-[#FEFEFE] font-[Inter] text-center block">
                    NDPR Data Compliant, {new Date().getFullYear()}.
                  </span>
                </div>
              </a>

              <div className="flex items-center gap-6 text-white">
                <a
                  href={instagram}
                  aria-label="Instagram"
                  className="hover:text-white/80"
                >
                  <FaInstagram className="w-6 h-6 lg:w-8 lg:h-8" />
                </a>
                <a
                  href={twitter}
                  aria-label="Twitter"
                  className="hover:text-white/80"
                >
                  <FaTwitter className="w-6 h-6 lg:w-8 lg:h-8" />
                </a>
                <a
                  href={linkedin}
                  aria-label="LinkedIn"
                  className="hover:text-white/80"
                >
                  <FaLinkedin className="w-6 h-6 lg:w-8 lg:h-8" />
                </a>
                <a
                  href={facebook}
                  aria-label="Facebook"
                  className="hover:text-white/80"
                >
                  <FaFacebook className="w-6 h-6 lg:w-8 lg:h-8" />
                </a>
              </div>
            </div>
          </div>

          <div className="text-center text-sm lg:text-base text-white/80 pb-6">
            {`${new Date().getFullYear()} \u00A9 LaborHack. All Rights Reserved.`}
          </div>
        </div>
      </div>
    </footer>
  );
};
